// 在庫調整（破損・ロス・理由コード付き）— Stocky parity v2 Step 1。
// 全プランで利用可（Free の「手動運用」の一部）。v1 は主拠点固定＝拠点セレクタは
// 出さない（Business の拠点選択・操作履歴は audit log ステップで配線する）。
// 登録すると Shopify の在庫へ即時反映されるため、実行前に window.confirm を挟む
// （「注文済みにする」の確認と同じ idiom・Polaris モーダルを増やさない）。
import {
  Autocomplete,
  Banner,
  BlockStack,
  Button,
  Card,
  DataTable,
  Layout,
  Page,
  Select,
  Text,
  TextField,
} from "@shopify/polaris";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ApiError } from "../api/client";
import {
  ADJUSTMENT_REASON_LABELS,
  canonicalPublicBigIntId,
  createInventoryAdjustment,
  getAdjustableVariantByShopifyId,
  listAdjustableVariants,
  listInventoryAdjustments,
} from "../api/inventory_adjustments";
import type { AdjustableVariant } from "../api/inventory_adjustments";
import { QueryErrorBanner } from "../components/QueryErrorBanner";
import { ScanInput } from "../components/ScanInput";
import { useVerifiedShopGeneration } from "../ShopGenerationBoundary";

import { t } from "../lib/i18n";
import { parseQuantityInput } from "../utils/quantityInput";
import {
  differingRecordedProductTitle,
  displayVariantTitle,
  hasCurrentProductTitleProjection,
  merchantSafeRecordedProductTitle,
  projectedProductTitle,
} from "../utils/productTitles";
import { formatDateTime } from "../utils/date";
import {
  clearPersistentIdempotencyAttempt,
  persistentIdempotencyAttempt,
  type PersistentIdempotencyAttempt,
} from "../utils/idempotency";

const ADJUSTMENT_ATTEMPT_SCOPE = "inventory-adjustment:create";
const VARIANT_SEARCH_DEBOUNCE_MS = 300;
const VARIANT_SEARCH_LIMIT = 100;

function isDefinitiveClientFailure(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  // API契約上、副作用前の入力拒否と断定できるstatusだけをclearする。
  // 認証・認可・競合・rate-limit・未分類4xxは結果が曖昧なので保持する。
  return [400, 404, 422].includes(error.status);
}

function variantLabel(v: {
  product_title: string | null;
  title: string | null;
  sku: string | null;
  available: number | null;
}): string {
  const name = displayVariantTitle(v.product_title, v.title);
  const sku = v.sku ? `（SKU: ${v.sku}）` : "";
  const stock =
    v.available !== null ? t(" — 在庫 {n}", { n: v.available }) : "";
  return `${name || t("(名称未設定)")}${sku}${stock}`;
}

function variantSearchLabel(v: {
  shopify_variant_id: string;
  product_title: string | null;
  title: string | null;
  sku: string | null;
}): string {
  return t("{title} / SKU: {sku} / Shopify バリアントID: {id}", {
    title: displayVariantTitle(v.product_title, v.title) || t("(名称未設定)"),
    sku: v.sku ?? "—",
    id: v.shopify_variant_id,
  });
}

function uniqueVariants(rows: AdjustableVariant[]): AdjustableVariant[] {
  const byId = new Map<string, AdjustableVariant>();
  for (const row of rows) byId.set(row.shopify_variant_id, row);
  return [...byId.values()];
}

function parseRequestedVariantId(value: string | null): string | null {
  if (value === null) return null;
  try {
    return canonicalPublicBigIntId(value, "variant_id");
  } catch {
    return null;
  }
}

export function InventoryAdjustments() {
  const queryClient = useQueryClient();
  const { shopGeneration } = useVerifiedShopGeneration();
  const [searchParams] = useSearchParams();
  const requestedVariantIdRaw = searchParams.get("variant_id");
  const requestedVariantId = parseRequestedVariantId(requestedVariantIdRaw);
  const targetRequestKey =
    requestedVariantIdRaw === null
      ? null
      : `${shopGeneration}:${requestedVariantIdRaw}`;
  const [variantId, setVariantId] = useState("");
  const [variantSearch, setVariantSearch] = useState("");
  const [selectedSearchLabel, setSelectedSearchLabel] = useState("");
  const [selectedSearchIds, setSelectedSearchIds] = useState<string[]>([]);
  const [debouncedVariantSearch, setDebouncedVariantSearch] = useState("");
  const [searched, setSearched] = useState<AdjustableVariant[]>([]);
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("damaged");
  const [note, setNote] = useState("");
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // スキャンで解決した商品。一覧（500件で頭打ち）に載っていない商品を読んだときに
  // セレクトの選択肢が無く選べなくなるため、読んだ分だけ選択肢へ足す。
  const [scanned, setScanned] = useState<AdjustableVariant[]>([]);
  const submitAttempt = useRef<PersistentIdempotencyAttempt | null>(null);
  const appliedTargetRequest = useRef<string | null>(null);

  const variantsQuery = useQuery({
    queryKey: ["adjustable-variants", "adjustment", shopGeneration],
    queryFn: () => listAdjustableVariants(),
  });
  const variantSearchTerm =
    selectedSearchLabel !== "" && variantSearch === selectedSearchLabel
      ? ""
      : variantSearch.trim();
  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedVariantSearch(variantSearchTerm),
      VARIANT_SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [variantSearchTerm]);
  const variantSearchQuery = useQuery({
    queryKey: [
      "adjustable-variants",
      "adjustment-search",
      shopGeneration,
      debouncedVariantSearch,
    ],
    queryFn: () =>
      listAdjustableVariants(debouncedVariantSearch, VARIANT_SEARCH_LIMIT),
    enabled: debouncedVariantSearch !== "",
  });
  const targetVariantQuery = useQuery({
    queryKey: [
      "adjustable-variants",
      "adjustment-target",
      shopGeneration,
      requestedVariantId,
    ],
    queryFn: () =>
      getAdjustableVariantByShopifyId(requestedVariantId as string),
    enabled: requestedVariantId !== null,
    retry: false,
  });
  // URLは遷移先を示すだけで、商品名・SKU・在庫数は一切受け取らない。店舗世代と
  // IDが変わった瞬間に旧選択を落とし、tenant-scoped APIの完全一致が返った後だけ
  // 選択する。数量・理由・メモは変更せず、登録操作も自動実行しない。
  useEffect(() => {
    if (targetRequestKey === null) return;
    appliedTargetRequest.current = null;
    setVariantId("");
    setVariantSearch("");
    setSelectedSearchLabel("");
    setSelectedSearchIds([]);
  }, [targetRequestKey]);
  useEffect(() => {
    if (
      targetRequestKey === null ||
      !targetVariantQuery.isSuccess ||
      appliedTargetRequest.current === targetRequestKey
    ) {
      return;
    }
    appliedTargetRequest.current = targetRequestKey;
    const variant = targetVariantQuery.data;
    if (!variant) return;
    setSearched((previous) => uniqueVariants([...previous, variant]));
    const id = variant.shopify_variant_id;
    const label = variantSearchLabel(variant);
    setVariantId(id);
    setVariantSearch(label);
    setSelectedSearchLabel(label);
    setSelectedSearchIds([id]);
  }, [targetRequestKey, targetVariantQuery.data, targetVariantQuery.isSuccess]);
  const adjustmentsQuery = useQuery({
    queryKey: ["inventory-adjustments"],
    queryFn: listInventoryAdjustments,
  });

  const mutation = useMutation({
    mutationFn: ({
      input,
      attempt,
    }: {
      input: Parameters<typeof createInventoryAdjustment>[0];
      attempt: PersistentIdempotencyAttempt;
    }) => createInventoryAdjustment(input, attempt.key),
    onSuccess: (_adjustment, variables) => {
      clearPersistentIdempotencyAttempt(
        ADJUSTMENT_ATTEMPT_SCOPE,
        variables.attempt,
      );
      submitAttempt.current = null;
      setSuccessMessage(
        t("在庫調整を記録しました。Shopify の在庫にも反映済みです。"),
      );
      setErrorMessage(null);
      setDelta("");
      setNote("");
      queryClient.invalidateQueries({ queryKey: ["inventory-adjustments"] });
      queryClient.invalidateQueries({ queryKey: ["adjustable-variants"] });
    },
    onError: (err: unknown, variables) => {
      if (isDefinitiveClientFailure(err)) {
        clearPersistentIdempotencyAttempt(
          ADJUSTMENT_ATTEMPT_SCOPE,
          variables.attempt,
        );
        submitAttempt.current = null;
      }
      setSuccessMessage(null);
      setErrorMessage(err instanceof Error ? err.message : String(err));
    },
  });

  const listed = variantsQuery.data ?? [];
  // スキャンで読んだが一覧に無い商品を末尾に足す（available は一覧にしか無いので null）。
  const scannedOnly = scanned
    .filter(
      (s) => !listed.some((v) => v.shopify_variant_id === s.shopify_variant_id),
    )
    .map((s) => ({
      shopify_variant_id: s.shopify_variant_id,
      sku: s.sku,
      title: s.title,
      product_title: s.product_title,
      available: null,
    }));
  const variants = uniqueVariants([...listed, ...scannedOnly, ...searched]);
  const variantOptions = [
    { label: t("商品を選択してください"), value: "" },
    ...variants.map((v) => ({
      label: variantLabel(v),
      value: v.shopify_variant_id,
    })),
  ];
  const reasonOptions = Object.entries(ADJUSTMENT_REASON_LABELS).map(
    ([value, label]) => ({ label: t(label), value }),
  );
  const selectedSearchVariant = variants.find(
    (variant) => variant.shopify_variant_id === selectedSearchIds[0],
  );
  // 打鍵後300msの待機中は、直前の検索結果を候補として出さない。遅い旧応答が
  // あとから返っても query key が異なるので、新しい検索結果へ混ざらない。
  const searchCandidates = uniqueVariants(
    selectedSearchVariant && variantSearch === selectedSearchLabel
      ? [selectedSearchVariant]
      : variantSearchTerm === ""
        ? variants.slice(0, VARIANT_SEARCH_LIMIT)
        : variantSearchTerm === debouncedVariantSearch
          ? (variantSearchQuery.data ?? [])
          : [],
  );
  const searchOptions = searchCandidates.map((variant) => ({
    value: variant.shopify_variant_id,
    label: variantSearchLabel(variant),
  }));

  // 在庫調整は減算があるので負数を許す（棚卸し・移動とはここだけ違う）。
  const deltaInput = parseQuantityInput(delta, { allowNegative: true });
  const parsedDelta =
    deltaInput.kind === "value" ? deltaInput.value : Number.NaN;
  const canSubmit =
    variantId !== "" &&
    deltaInput.kind === "value" &&
    parsedDelta !== 0 &&
    !mutation.isPending;

  const handleSubmit = () => {
    const variant = variants.find((v) => v.shopify_variant_id === variantId);
    if (!variant) return;
    const sign = parsedDelta > 0 ? `+${parsedDelta}` : String(parsedDelta);
    const ok = window.confirm(
      t("{item} の在庫を {sign} 調整します。", {
        item: variantLabel(variant),
        sign,
      }) + t("Shopify の在庫に即時反映されます。よろしいですか？"),
    );
    if (!ok) return;
    const input = {
      reason,
      note: note.trim() === "" ? undefined : note.trim(),
      items: [
        { shopify_variant_id: variant.shopify_variant_id, delta: parsedDelta },
      ],
    };
    const attempt = persistentIdempotencyAttempt({
      scope: ADJUSTMENT_ATTEMPT_SCOPE,
      shopGeneration,
      previous: submitAttempt.current,
      fingerprint: JSON.stringify(input),
    });
    submitAttempt.current = attempt;
    mutation.mutate({ input, attempt });
  };

  const adjustments = adjustmentsQuery.data ?? [];
  const historyRows = adjustments.map((adjustment) => [
    formatDateTime(adjustment.applied_at),
    adjustment.location_name ?? "—",
    t(ADJUSTMENT_REASON_LABELS[adjustment.reason] ?? adjustment.reason),
    adjustment.items
      .map((item) => {
        const sign = item.delta > 0 ? `+${item.delta}` : String(item.delta);
        const range =
          item.before_qty !== null && item.after_qty !== null
            ? `（${item.before_qty}→${item.after_qty}）`
            : "";
        const hasCurrent = hasCurrentProductTitleProjection(item);
        const safeRecorded = merchantSafeRecordedProductTitle(item);
        const current = hasCurrent
          ? (projectedProductTitle(item) ?? item.shopify_variant_id)
          : (safeRecorded ?? item.sku ?? item.shopify_variant_id);
        const sku =
          item.sku && (hasCurrent || safeRecorded)
            ? `（SKU: ${item.sku}）`
            : "";
        const recorded = differingRecordedProductTitle(item);
        const recordedLabel = recorded
          ? ` [${t("保存時の商品バリエーション名")}: ${recorded}]`
          : "";
        return `${current}${sku}${recordedLabel}: ${sign}${range}`;
      })
      .join(" / "),
    adjustment.note ?? "",
  ]);

  return (
    <Page title={t("在庫調整")}>
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {successMessage && (
              <Banner tone="success" onDismiss={() => setSuccessMessage(null)}>
                {successMessage}
              </Banner>
            )}
            {errorMessage && (
              <Banner
                tone="critical"
                title={t("在庫調整に失敗しました")}
                onDismiss={() => setErrorMessage(null)}
              >
                {errorMessage}
              </Banner>
            )}
            {(variantsQuery.isError || adjustmentsQuery.isError) && (
              <QueryErrorBanner
                title={t("在庫調整の情報の取得に失敗しました")}
                onRetry={() => {
                  variantsQuery.refetch();
                  adjustmentsQuery.refetch();
                }}
              />
            )}
            {variantSearchQuery.isError && (
              <QueryErrorBanner
                title={t("商品検索に失敗しました")}
                onRetry={() => variantSearchQuery.refetch()}
              />
            )}
            {requestedVariantIdRaw !== null && requestedVariantId === null && (
              <Banner
                tone="critical"
                title={t("指定された商品を選択できませんでした")}
              >
                {t(
                  "URLの商品IDが正しくありません。在庫調整する商品を検索して選んでください。",
                )}
              </Banner>
            )}
            {requestedVariantId !== null && targetVariantQuery.isError && (
              <QueryErrorBanner
                title={t("指定された商品を確認できませんでした")}
                onRetry={() => targetVariantQuery.refetch()}
              />
            )}
            {requestedVariantId !== null &&
              targetVariantQuery.isSuccess &&
              targetVariantQuery.data === null && (
                <Banner
                  tone="critical"
                  title={t("指定された商品を選択できませんでした")}
                >
                  {t(
                    "指定された商品は現在のShopify商品に見つからないか、このストアの商品ではありません。商品を検索して選んでください。",
                  )}
                </Banner>
              )}
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  {t("調整を記録する")}
                </Text>
                <Text as="p" tone="subdued">
                  {t(
                    "破損・ロスなどの在庫の増減を理由つきで記録します。登録すると Shopify の在庫（主拠点）に即時反映されます — Shopify 管理画面で数量を直接編集するのと同じ効果です。同じ調整を両方で行うと二重に増減するため、どちらか一方で行ってください。",
                  )}
                </Text>
                <ScanInput
                  onResolved={(variant) => {
                    // 🔴 調整はスキャンで「商品を特定するだけ」＝数量は加算しない。
                    // 数量の増減は符号つきの手入力で、スキャンごとに自動で動かすと
                    // -3 が -4 に化けても気づけない（数える画面＝棚卸し・入荷とは
                    // 意味が違うので、あえて挙動を揃えない）。
                    let canonicalId: string;
                    try {
                      canonicalId = canonicalPublicBigIntId(
                        variant.shopify_variant_id,
                        "shopify_variant_id",
                      );
                    } catch {
                      setErrorMessage(
                        t("読み取った商品のShopify IDが正しくありません。"),
                      );
                      return;
                    }
                    const adjustable: AdjustableVariant = {
                      ...variant,
                      shopify_variant_id: canonicalId,
                      available: null,
                    };
                    setScanned((prev) =>
                      prev.some((p) => p.shopify_variant_id === canonicalId)
                        ? prev
                        : [...prev, adjustable],
                    );
                    setVariantId(canonicalId);
                    const label = variantSearchLabel(adjustable);
                    setVariantSearch(label);
                    setSelectedSearchLabel(label);
                    setSelectedSearchIds([canonicalId]);
                  }}
                  helpText={t(
                    "読み取ると下の「商品」が切り替わります。数量は手で入力してください（スキャンでは増減しません）。",
                  )}
                />
                <Autocomplete
                  options={searchOptions}
                  selected={selectedSearchIds}
                  onSelect={(selected) => {
                    const selectedId = selected[0];
                    const variant = searchCandidates.find(
                      (candidate) =>
                        candidate.shopify_variant_id === selectedId,
                    );
                    if (!selectedId || !variant) return;
                    setSearched((previous) =>
                      uniqueVariants([...previous, variant]),
                    );
                    setVariantId(selectedId);
                    setSelectedSearchIds([selectedId]);
                    const label = variantSearchLabel(variant);
                    setVariantSearch(label);
                    setSelectedSearchLabel(label);
                  }}
                  loading={
                    variantsQuery.isLoading ||
                    variantSearchTerm !== debouncedVariantSearch ||
                    variantSearchQuery.isFetching
                  }
                  listTitle={t("在庫調整する商品候補")}
                  emptyState={
                    <Text as="p" tone="subdued">
                      {t("条件に一致する商品はありません")}
                    </Text>
                  }
                  textField={
                    <Autocomplete.TextField
                      label={t(
                        "商品を検索（SKU・商品名・Shopify バリアントID）",
                      )}
                      value={variantSearch}
                      onChange={(value) => {
                        setVariantSearch(value);
                        if (value !== selectedSearchLabel) {
                          setSelectedSearchLabel("");
                          setSelectedSearchIds([]);
                          setVariantId("");
                        }
                      }}
                      placeholder={t("商品名 / SKU / Shopify バリアントID")}
                      autoComplete="off"
                      clearButton
                      onClearButtonClick={() => {
                        setVariantSearch("");
                        setSelectedSearchLabel("");
                        setSelectedSearchIds([]);
                        setVariantId("");
                      }}
                    />
                  }
                />
                <Select
                  label={t("商品")}
                  options={variantOptions}
                  value={variantId}
                  onChange={(value) => {
                    setVariantId(value);
                    const variant = variants.find(
                      (candidate) => candidate.shopify_variant_id === value,
                    );
                    if (!variant) {
                      setVariantSearch("");
                      setSelectedSearchLabel("");
                      setSelectedSearchIds([]);
                      return;
                    }
                    const label = variantSearchLabel(variant);
                    setVariantSearch(label);
                    setSelectedSearchLabel(label);
                    setSelectedSearchIds([value]);
                  }}
                  disabled={variantsQuery.isLoading}
                />
                <TextField
                  label={t("数量の増減")}
                  type="integer"
                  value={delta}
                  onChange={setDelta}
                  autoComplete="off"
                  helpText={t(
                    "減らすときはマイナス（例: -3）。0 は指定できません。",
                  )}
                />
                <Select
                  label={t("理由")}
                  options={reasonOptions}
                  value={reason}
                  onChange={setReason}
                />
                <TextField
                  label={t("メモ（任意）")}
                  value={note}
                  onChange={setNote}
                  autoComplete="off"
                  maxLength={2000}
                />
                <div>
                  <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    loading={mutation.isPending}
                  >
                    {t("調整を記録する")}
                  </Button>
                </div>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  {t("調整履歴")}
                </Text>
                <Text as="p" tone="subdued">
                  {t(
                    "この履歴は UreyukiBox から行った調整のみです。Shopify 管理画面で直接編集した変動はここには並びません（在庫数そのものには同期で反映されます）。",
                  )}
                </Text>
                {historyRows.length === 0 ? (
                  <Text as="p" tone="subdued">
                    {t("調整履歴はまだありません。")}
                  </Text>
                ) : (
                  <DataTable
                    columnContentTypes={[
                      "text",
                      "text",
                      "text",
                      "text",
                      "text",
                    ]}
                    headings={[
                      t("日時"),
                      t("拠点"),
                      t("理由"),
                      t("内容"),
                      t("メモ"),
                    ]}
                    rows={historyRows}
                  />
                )}
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
